import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import { Box, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ICellRendererParams } from 'ag-grid-community';
import { isArray, isNumber } from 'lodash';
import { useMemo } from 'react';
import { isStockOptimalOrCOGS } from 'src/components/views/StockManagement/helpers/module-check';
import { ComparableCell } from '../ComparableCell/ComparableCell';

// custom params to be used with cellRendererParams
type EditablePercentageCellParams = ICellRendererParams & {
    ignoreLast?: boolean;
    spacing?: number;
    isStockManagement?: boolean;
    isOptimalOptionCount?: boolean;
    isModifiedByUserGetter?: (params: ICellRendererParams) => boolean | undefined;
};

interface Props {
    componentProps: EditablePercentageCellParams;
    isStockManagement?: boolean;
    isOptimalOptionCount?: boolean;
    isModifiedByUserGetter?: (params: ICellRendererParams) => boolean | undefined;
}

export function EditablePercentageCell(componentProps: EditablePercentageCellParams) {
    const { node, valueFormatted, ignoreLast } = componentProps;

    const isSumRow = node.lastChild && (node?.data?.category === 'Sum' || node?.data?.gender === 'Sum');
    const isFooter = node.footer;
    const isRowGroupParent = node.key;

    if (isSumRow || isFooter || (node.lastChild && ignoreLast === false)) {
        return <NonEditableCellWrapper>{valueFormatted}</NonEditableCellWrapper>;
    }

    if (isRowGroupParent) {
        return <SpacedNonEditableCellWrapper>{valueFormatted}</SpacedNonEditableCellWrapper>;
    }

    return <EditableCell componentProps={componentProps} />;
}

export function EditableValueCell(componentProps: EditablePercentageCellParams) {
    const { node, data, valueFormatted, isStockManagement, isOptimalOptionCount, colDef, column, api, context } =
        componentProps;
    const isFooter = node?.data?.category === 'Total' || node?.data?.isFooter;
    const isRowGroupParent = node.key;
    const valueIsUnlimited = isStockManagement && valueFormatted === null;

    const editable = useMemo(() => {
        if (!colDef) {
            return false;
        }
        if (colDef.editable === undefined) {
            return true;
        }
        if (typeof colDef.editable === 'boolean') {
            return colDef.editable;
        }
        if (typeof colDef.editable === 'function' && column) {
            return colDef.editable({ node, data, column, colDef, api, context });
        }
        return false;
    }, [colDef, column, data, api, context, node]);

    if (!editable) {
        return <NonEditableCellWrapper>{valueFormatted}</NonEditableCellWrapper>;
    }

    if (isStockManagement && isStockOptimalOrCOGS(node.data?.category)) return <ComparableCell {...componentProps} />;

    if (isRowGroupParent || (isFooter && isOptimalOptionCount) || !editable) {
        return <SpacedNonEditableCellWrapper>{valueFormatted}</SpacedNonEditableCellWrapper>;
    }

    if (isFooter) {
        return <NonEditableCellWrapper>{valueIsUnlimited ? 'Unlimited' : valueFormatted}</NonEditableCellWrapper>;
    }

    return (
        <EditableCell
            componentProps={componentProps}
            isStockManagement={isStockManagement}
            isOptimalOptionCount={isOptimalOptionCount}
        />
    );
}

function EditableCell(props: Props) {
    const {
        valueFormatted,
        api,
        column,
        value: currentValue,
        node,
        isStockManagement,
        isOptimalOptionCount,
        isModifiedByUserGetter,
    } = props.componentProps;
    const isFooter = node.footer;

    const isModifiedByUser = useMemo(
        () => isModifiedByUserGetter?.(props.componentProps),
        [isModifiedByUserGetter, props.componentProps]
    );

    const valueFormattedIsArray = isArray(valueFormatted);
    const valueFormattedIsNull = valueFormattedIsArray ? valueFormatted[0] === null : valueFormatted === null;
    const valueFormattedIsEmptyString = valueFormattedIsArray ? valueFormatted[0] === '' : valueFormatted === '';

    const valueIsUnlimited = isStockManagement && (valueFormattedIsNull || valueFormattedIsEmptyString);
    const valueIsEmpty = isOptimalOptionCount && (valueFormattedIsNull || valueFormattedIsEmptyString);

    const value = useMemo(() => {
        if (valueIsUnlimited) return 'Unlimited';
        if (valueIsEmpty || valueFormattedIsEmptyString) return '';
        if (valueFormattedIsArray) return valueFormatted[0];
        return valueFormatted ?? currentValue;
    }, [
        currentValue,
        valueFormatted,
        valueFormattedIsArray,
        valueFormattedIsEmptyString,
        valueIsEmpty,
        valueIsUnlimited,
    ]);
    if (isFooter) return <SpacedNonEditableCellWrapper>{valueFormatted}</SpacedNonEditableCellWrapper>;

    return (
        <EditableCellWrapper valueIsUnlimited={valueIsUnlimited ?? valueIsEmpty}>
            {typeof isModifiedByUser === 'boolean' && isModifiedByUser && (
                <LockIcon sx={{ fontSize: 14, marginRight: 'auto' }} color="primary" />
            )}
            {value}
            <IconButton
                color="info"
                size="small"
                onClick={() => {
                    if (column !== undefined && isNumber(node.rowIndex)) {
                        api.startEditingCell({
                            rowIndex: node.rowIndex,
                            colKey: column,
                        });
                    }
                }}
            >
                <EditIcon fontSize="inherit" />
            </IconButton>
        </EditableCellWrapper>
    );
}

const EditableCellWrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'valueIsUnlimited',
})<{ valueIsUnlimited?: boolean }>(({ theme, valueIsUnlimited }) => ({
    width: '100%',
    display: 'flex',
    justifyContent: 'right',
    alignItems: 'center',
    cursor: 'pointer',
    ...(valueIsUnlimited
        ? {}
        : {
              color: `var(--warning-color--editable, ${theme.palette.info.main})`,
          }),
}));

const NonEditableCellWrapper = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'flex-end',
}));

const SpacedNonEditableCellWrapper = styled(NonEditableCellWrapper)`
  padding-right: 19px;
`;
